<template>
  <div class="tin-root">
    <div class="ambient"></div>
    <div class="ambient-floor"></div>
    <div class="grain"></div>
    <div class="frame"></div>
    <div class="frame-inner"></div>

    <div class="page">

      <!-- ── HEADER ── -->
      <div class="settings-header">
        <div>
          <div class="section-hd">
            <div class="section-rule l"></div>
            <span class="section-eyebrow">Account</span>
            <div class="section-rule"></div>
          </div>
          <h2 class="section-title">Settings</h2>
        </div>
        <button class="refresh-btn" @click="refresh" title="Refresh">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
        </button>
      </div>

      <!-- ── PROFILE CARD ── -->
      <div class="profile-card">
        <div class="profile-av-ring">
          <div class="profile-av-inner">
            <img v-if="profile.image" :src="profile.image" alt="" />
            <span v-else class="profile-monogram">{{ initials(profile.name) }}</span>
          </div>
        </div>

        <div class="profile-info">
          <p class="profile-name">{{ profile.name || 'Member' }}</p>
          <p v-if="profile.jobTitle || profile.company" class="profile-role">
            {{ profile.jobTitle }}<span v-if="profile.jobTitle && profile.company"> · </span>{{ profile.company }}
          </p>

          <div class="profile-meta-row">
            <span v-if="profile.club" class="profile-meta-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="2" y="7" width="20" height="14" rx="1"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg>
              {{ profile.club }}
            </span>
            <span v-if="profile.frequentCities" class="profile-meta-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 21s-7-6.2-7-11.5A7 7 0 0119 9.5C19 14.8 12 21 12 21z"/><circle cx="12" cy="9.5" r="2.3"/></svg>
              {{ profile.frequentCities }}
            </span>
            <span v-if="profile.email" class="profile-meta-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 4h16v16H4z" opacity="0"/><path d="M22 6l-10 7L2 6"/><rect x="2" y="4" width="20" height="16" rx="2"/></svg>
              {{ profile.email }}
            </span>
          </div>

          <div class="completion-row">
            <div class="completion-bar-wrap">
              <div class="completion-bar" :style="{ width: completionPercent + '%' }"></div>
            </div>
            <span class="completion-pct">{{ completionPercent }}%</span>
          </div>
          <p class="completion-label">Profile completion · {{ completionMissingCount }} field{{ completionMissingCount !== 1 ? 's' : '' }} missing</p>

          <div class="quick-actions">
            <button class="qa-btn" @click="go('profileInfo')">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              Profile
            </button>
            <button class="qa-btn" @click="$router.push('/groups')">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
              Chapters
            </button>
            <button class="qa-btn" @click="go('security')">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              Security
            </button>
          </div>
        </div>
      </div>

      <!-- ── MISSING FIELDS HINT ── -->
      <div v-if="completionPercent < 100 && !loading" class="missing-hint" @click="go('personalInfo')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        <span>Complete your profile — add {{ missingFieldLabels }}</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polyline points="9 18 15 12 9 6"/></svg>
      </div>

      <!-- ── LOADING ── -->
      <div v-if="loading" class="sk-list">
        <div v-for="n in 5" :key="'sk-' + n" class="sk-item"></div>
      </div>

      <template v-else>

        <!-- ── ACCOUNT ── -->
        <div class="sec-head">
          <div class="section-rule l"></div>
          <span class="sec-eyebrow">Account</span>
          <div class="section-rule"></div>
        </div>

        <div class="settings-list">
          <div class="settings-item" @click="go('personalInfo')">
            <div class="si-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="2" y="4" width="20" height="16" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
            </div>
            <div class="si-content">
              <p class="si-title">Personal Information</p>
              <p class="si-sub">Name, email, phone &amp; club details</p>
            </div>
            <span v-if="completionPercent < 100" class="si-badge">{{ 100 - completionPercent }}% left</span>
            <svg class="si-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polyline points="9 18 15 12 9 6"/></svg>
          </div>

          <div class="settings-item" @click="go('profileInfo')">
            <div class="si-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            </div>
            <div class="si-content">
              <p class="si-title">Profile</p>
              <p class="si-sub">Photo, bio, home club &amp; frequent cities</p>
            </div>
            <svg class="si-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polyline points="9 18 15 12 9 6"/></svg>
          </div>
        </div>

        <!-- ── SECURITY ── -->
        <div class="sec-head">
          <div class="section-rule l"></div>
          <span class="sec-eyebrow">Security</span>
          <div class="section-rule"></div>
        </div>

        <div class="settings-list">
          <div class="settings-item" @click="go('security')">
            <div class="si-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
            </div>
            <div class="si-content">
              <p class="si-title">Security Settings</p>
              <p class="si-sub">Password, sessions</p>
            </div>
            <svg class="si-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polyline points="9 18 15 12 9 6"/></svg>
          </div>

          <div class="settings-item" @click="go('notifications')">
            <div class="si-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>
            </div>
            <div class="si-content">
              <p class="si-title">Notifications</p>
              <p class="si-sub">Chapters, comments &amp; arrangement requests</p>
            </div>
            <svg class="si-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polyline points="9 18 15 12 9 6"/></svg>
          </div>
        </div>

        <!-- ── SUPPORT ── -->
        <div class="sec-head">
          <div class="section-rule l"></div>
          <span class="sec-eyebrow">Support</span>
          <div class="section-rule"></div>
        </div>

        <div class="settings-list">
          <div class="settings-item" @click="go('help')">
            <div class="si-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            </div>
            <div class="si-content">
              <p class="si-title">Help &amp; FAQ</p>
              <p class="si-sub">Get answers and support</p>
            </div>
            <svg class="si-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polyline points="9 18 15 12 9 6"/></svg>
          </div>

          <div class="settings-item" @click="go('about')">
            <div class="si-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
            </div>
            <div class="si-content">
              <p class="si-title">About The Inner Network</p>
              <p class="si-sub">Version, terms, privacy</p>
            </div>
            <svg class="si-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polyline points="9 18 15 12 9 6"/></svg>
          </div>
        </div>

        <!-- ── LOGOUT ── -->
        <div class="logout-wrap">
          <button class="logout-btn" @click="logout">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            Sign Out
          </button>
        </div>

        <!-- ── DANGER ZONE ── -->
        <div class="danger-zone">
          <div class="section-hd">
            <div class="section-rule l"></div>
            <span class="section-eyebrow danger">Danger Zone</span>
            <div class="section-rule"></div>
          </div>
          <p class="danger-text">
            Deleting your account is permanent and immediate. All your recommendations,
            travel plans, messages, and connections are removed right away, and this
            cannot be undone.
          </p>
          <button class="delete-account-btn" @click="showDeleteConfirm = true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
            Request Account Deletion
          </button>
        </div>

      </template>
    </div>

    <!-- ── DELETE CONFIRMATION MODAL ── -->
    <div v-if="showDeleteConfirm" class="modal-overlay" @click.self="closeDeleteConfirm">
      <div class="modal-panel">
        <h3 class="modal-title">Delete Your Account?</h3>
        <p class="modal-text">
          This will permanently and immediately remove your profile, recommendations,
          travel plans, messages, and connections. This cannot be undone.
        </p>
        <p class="modal-text modal-confirm-label">
          Type <strong>DELETE</strong> to confirm:
        </p>
        <input
          v-model="deleteConfirmText"
          class="modal-input"
          type="text"
          autocomplete="off"
          @keyup.enter="requestAccountDeletion"
        />
        <div class="modal-actions">
          <button class="modal-cancel" @click="closeDeleteConfirm">Cancel</button>
          <button
            class="modal-confirm"
            :disabled="deleteConfirmText !== 'DELETE' || deletionInProgress"
            @click="requestAccountDeletion"
          >
            {{ deletionInProgress ? 'Submitting…' : 'Delete My Account' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── BOTTOM NAV ── -->
    <nav class="bottom-nav">
      <div class="bottom-nav-glow"></div>
      <button class="nav-item" @click="$router.push('/home')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
        <span class="nav-label">Home</span>
      </button>
      <button class="nav-item" @click="$router.push('/search')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <span class="nav-label">Search</span>
      </button>
      <button class="nav-item" @click="$router.push('/explore')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>
        <span class="nav-label">Explore</span>
      </button>
      <button class="nav-item" @click="$router.push('/messages')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
        <span class="nav-label">Messages</span>
      </button>
      <button class="nav-item" @click="$router.push('/groups')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
        <span class="nav-label">Groups</span>
      </button>
      <button class="nav-item active" @click="$router.push('/member-settings')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
        <span class="nav-label">Profile</span>
        <div class="nav-dot"></div>
      </button>
    </nav>

    <!-- ── TOAST ── -->
    <div v-if="snack.show" class="toast" :class="snack.color">{{ snack.text }}</div>
  </div>
</template>

<script>
import { doc, getDoc } from "firebase/firestore";
import { db, auth, functions } from "@/firebase";
import { signOut } from "firebase/auth";
import { httpsCallable } from "firebase/functions";

// All tracked fields with their label and weight — matches the fields
// actually used by Profile.vue (club/jobTitle/company/frequentCities),
// not a generic corporate-org profile.
const FIELDS = [
  { key: "fullName",       label: "full name",       weight: 15 },
  { key: "image",          label: "profile photo",   weight: 15 },
  { key: "email",          label: "email",           weight: 10 },
  { key: "phone",          label: "phone",           weight: 5 },
  { key: "gender",         label: "gender",          weight: 5 },
  { key: "description",    label: "bio",             weight: 10 },
  { key: "club",           label: "home club",       weight: 12 },
  { key: "jobTitle",       label: "job title",       weight: 8 },
  { key: "company",        label: "company",         weight: 8 },
  { key: "frequentCities", label: "frequent cities", weight: 12 },
];

export default {
  name: "MemberSettingsView",

  data() {
    return {
      loading: true,
      profile: {
        image: "", name: "", email: "", club: "", jobTitle: "", company: "",
        frequentCities: "", description: "", phone: "", gender: "",
      },
      snack: { show: false, text: "", color: "info" },
      showDeleteConfirm: false,
      deleteConfirmText: "",
      deletionInProgress: false,
    };
  },

  computed: {
    completionPercent() {
      const total = FIELDS.reduce((s, f) => s + f.weight, 0);
      const earned = FIELDS.reduce((s, f) => {
        const val = this.profile[f.key];
        return s + ((val && String(val).trim()) ? f.weight : 0);
      }, 0);
      return Math.round((earned / total) * 100);
    },

    missingFields() {
      return FIELDS.filter(f => {
        const val = this.profile[f.key];
        return !val || !String(val).trim();
      });
    },

    completionMissingCount() {
      return this.missingFields.length;
    },

    missingFieldLabels() {
      return this.missingFields.slice(0, 3).map(f => f.label).join(", ") +
        (this.missingFields.length > 3 ? ` +${this.missingFields.length - 3} more` : "");
    },
  },

  methods: {
    toast(text, color = "info") {
      this.snack = { show: true, text, color };
      setTimeout(() => { this.snack.show = false; }, 2500);
    },

    initials(name = "") {
      return name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
    },

    async fetchProfile() {
      try {
        const uid = auth.currentUser?.uid;
        if (!uid) return;
        const snap = await getDoc(doc(db, "user", uid));
        if (snap.exists()) {
          const d = snap.data();
          this.profile = {
            name:           d.fullName || "",
            image:          d.image || "",
            email:          d.email || "",
            club:           d.club || "",
            jobTitle:       d.jobTitle || "",
            company:        d.company || "",
            frequentCities: d.frequentCities || "",
            description:    d.description || "",
            // Personal Information saves this as "phone" — fall back to the
            // legacy "phoneNumber" field so accounts saved before that page
            // existed still count their number toward completion.
            phone:          d.phone || d.phoneNumber || "",
            gender:         d.gender || "",
          };
        }
      } catch (e) {
        console.error(e);
        this.toast("Failed to load profile", "error");
      } finally {
        this.loading = false;
      }
    },

    go(action) {
      // Only "personalInfo" and "profileInfo" map to routes confirmed to exist
      // in the router. The rest (security/notifications/help/about) are
      // included for when those pages exist — verify these route names
      // against your router before relying on them.
      const routes = {
        personalInfo:  "PersonalInformationView",
        profileInfo:   "Profile",
        security:      "SecuritySettingsView",
        notifications: "Notifications",
        help:          "HelpView",
        about:         "AboutView",
      };
      if (routes[action]) this.$router.push({ name: routes[action] });
    },

    async logout() {
      try {
        await signOut(auth);
        this.$router.push("/");
      } catch (e) {
        console.error(e);
        this.toast("Logout failed. Try again.", "error");
      }
    },

    closeDeleteConfirm() {
      this.showDeleteConfirm = false;
      this.deleteConfirmText = "";
    },

    // Calls deleteMyAccount (Cloud Function), which deletes every piece of
    // this member's data server-side via the Admin SDK — messages sent
    // and received, posts, explore posts and their Storage media, their
    // profile, connections, and finally the Auth account itself — then
    // signs out locally, since the account no longer exists to sign back
    // into.
    async requestAccountDeletion() {
      if (this.deleteConfirmText !== "DELETE" || this.deletionInProgress) return;
      const user = auth.currentUser;
      if (!user) return;

      this.deletionInProgress = true;
      try {
        const deleteMyAccount = httpsCallable(functions, "deleteMyAccount");
        await deleteMyAccount();
        this.closeDeleteConfirm();
        await signOut(auth);
        this.$router.push("/");
      } catch (e) {
        console.error(e);
        this.deletionInProgress = false;
        this.toast("Couldn't delete your account. Try again.", "error");
      }
    },

    refresh() {
      this.loading = true;
      this.fetchProfile().then(() => this.toast("Refreshed", "success"));
    },
  },

  mounted() {
    this.fetchProfile();
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');

.tin-root { position: relative; min-height: 100vh; width: 100%; background: #070708; font-family: 'Montserrat', sans-serif; }

.ambient { position: fixed; top: -300px; left: 50%; transform: translateX(-50%); width: 1000px; height: 800px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 10%, rgba(210,175,100,0.09) 0%, rgba(210,175,100,0.03) 30%, transparent 65%); }
.ambient-floor { position: fixed; bottom: -200px; left: 50%; transform: translateX(-50%); width: 700px; height: 400px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 100%, rgba(210,175,100,0.03) 0%, transparent 60%); }
.grain { position: fixed; inset: 0; z-index: 1; pointer-events: none; opacity: 0.5; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.12'/%3E%3C/svg%3E"); background-size: 180px 180px; }
.frame { position: fixed; inset: 20px; border: 1px solid rgba(210,175,100,0.07); z-index: 2; pointer-events: none; }
.frame::before, .frame::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }
.frame::after  { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner { position: fixed; inset: 20px; z-index: 2; pointer-events: none; }
.frame-inner::before, .frame-inner::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame-inner::before { top: -1px; right: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner::after  { bottom: -1px; left: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }

.page { position: relative; z-index: 10; max-width: 720px; margin: 0 auto; padding: 48px 24px 140px; animation: fadeUp 0.8s cubic-bezier(0.16,1,0.3,1) both; }
@keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

/* ── HEADER ── */
.settings-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 8px; }
.section-hd { display: flex; align-items: center; gap: 20px; margin-bottom: 16px; }
.section-rule { flex: 1; height: 1px; background: linear-gradient(90deg, rgba(210,175,100,0.18), transparent); }
.section-rule.l { background: linear-gradient(90deg, transparent, rgba(210,175,100,0.18)); flex: 0 0 24px; }
.section-eyebrow { font-size: 9px; font-weight: 600; letter-spacing: 4px; color: rgba(210,175,100,0.45); text-transform: uppercase; white-space: nowrap; }
.section-title { font-family: 'Cormorant', serif; font-size: 34px; font-weight: 300; color: #edeae2; letter-spacing: -0.5px; }
.refresh-btn { width: 38px; height: 38px; border: 1px solid rgba(210,175,100,0.2); background: transparent; color: rgba(210,175,100,0.5); display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; transition: border-color 0.2s, color 0.2s; margin-top: 20px; }
.refresh-btn:hover { border-color: rgba(210,175,100,0.6); color: rgba(210,175,100,0.9); }
.refresh-btn svg { width: 15px; height: 15px; }

/* ── PROFILE CARD ── */
.profile-card { border: 1px solid rgba(210,175,100,0.12); background: rgba(210,175,100,0.02); padding: 26px; display: flex; align-items: flex-start; gap: 20px; margin-bottom: 20px; position: relative; }
.profile-card::before, .profile-card::after { content: ''; position: absolute; width: 14px; height: 14px; }
.profile-card::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.4); border-left: 1px solid rgba(210,175,100,0.4); }
.profile-card::after { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.4); border-right: 1px solid rgba(210,175,100,0.4); }

.profile-av-ring { width: 84px; height: 84px; border-radius: 50%; border: 2px solid rgba(210,175,100,0.5); padding: 3px; flex-shrink: 0; }
.profile-av-inner { width: 100%; height: 100%; border-radius: 50%; background: rgba(210,175,100,0.05); overflow: hidden; display: flex; align-items: center; justify-content: center; }
.profile-av-inner img { width: 100%; height: 100%; object-fit: cover; }
.profile-monogram { font-family: 'Cormorant', serif; font-size: 28px; font-weight: 300; color: rgba(210,175,100,0.4); }

.profile-info { flex: 1; min-width: 0; }
.profile-name { font-family: 'Cormorant', serif; font-size: 26px; font-weight: 400; color: #edeae2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-bottom: 4px; }
.profile-role { font-size: 10.5px; color: rgba(210,175,100,0.6); letter-spacing: 1.5px; text-transform: uppercase; margin-bottom: 10px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.profile-meta-row { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 12px; }
.profile-meta-item { display: inline-flex; align-items: center; gap: 5px; font-size: 10px; color: rgba(237,232,223,0.35); letter-spacing: 0.3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 200px; }
.profile-meta-item svg { width: 11px; height: 11px; color: rgba(210,175,100,0.4); flex-shrink: 0; }

.completion-row { display: flex; align-items: center; gap: 10px; margin-bottom: 4px; }
.completion-bar-wrap { flex: 1; height: 4px; background: rgba(210,175,100,0.08); border-radius: 999px; overflow: hidden; }
.completion-bar { height: 100%; background: rgba(210,175,100,0.7); border-radius: 999px; transition: width 0.4s ease; }
.completion-pct { font-size: 11px; font-weight: 600; color: rgba(210,175,100,0.8); min-width: 32px; }
.completion-label { font-size: 8.5px; color: rgba(237,232,223,0.2); letter-spacing: 1.5px; text-transform: uppercase; margin-bottom: 16px; }

.quick-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.qa-btn { background: transparent; border: 1px solid rgba(210,175,100,0.2); border-radius: 20px; padding: 6px 14px; font-family: 'Montserrat', sans-serif; font-size: 10px; font-weight: 500; color: rgba(237,232,223,0.5); letter-spacing: 0.5px; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: border-color 0.2s, color 0.2s; }
.qa-btn svg { width: 12px; height: 12px; }
.qa-btn:hover { border-color: rgba(210,175,100,0.6); color: rgba(210,175,100,0.9); }

/* ── MISSING HINT ── */
.missing-hint { display: flex; align-items: center; gap: 10px; padding: 12px 18px; background: rgba(210,175,100,0.05); border: 1px solid rgba(210,175,100,0.15); font-size: 11px; color: rgba(237,232,223,0.5); cursor: pointer; transition: background 0.2s; margin-bottom: 32px; }
.missing-hint:hover { background: rgba(210,175,100,0.09); }
.missing-hint span { flex: 1; }
.missing-hint svg { width: 15px; height: 15px; color: rgba(210,175,100,0.5); flex-shrink: 0; }

/* ── SECTION HEADERS ── */
.sec-head { display: flex; align-items: center; gap: 16px; padding: 32px 0 14px; }
.sec-eyebrow { font-size: 9px; font-weight: 600; letter-spacing: 3px; text-transform: uppercase; color: rgba(210,175,100,0.45); white-space: nowrap; }

/* ── SETTINGS LIST ── */
.settings-list { border: 1px solid rgba(210,175,100,0.1); background: rgba(210,175,100,0.02); }
.settings-item { display: flex; align-items: center; gap: 16px; padding: 16px 18px; cursor: pointer; border-bottom: 1px solid rgba(210,175,100,0.08); transition: background 0.15s; }
.settings-item:last-child { border-bottom: none; }
.settings-item:hover { background: rgba(210,175,100,0.05); }

.si-icon { width: 36px; height: 36px; border: 1px solid rgba(210,175,100,0.2); display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: rgba(210,175,100,0.6); }
.si-icon svg { width: 16px; height: 16px; }
.si-content { flex: 1; min-width: 0; }
.si-title { font-size: 13px; font-weight: 500; color: #edeae2; margin-bottom: 2px; }
.si-sub { font-size: 10px; color: rgba(237,232,223,0.3); letter-spacing: 0.2px; }
.si-chevron { width: 15px; height: 15px; color: rgba(237,232,223,0.15); flex-shrink: 0; }

.si-badge { height: 18px; padding: 0 8px; background: rgba(210,175,100,0.1); border: 1px solid rgba(210,175,100,0.3); font-size: 8.5px; font-weight: 600; letter-spacing: 1px; text-transform: uppercase; color: rgba(210,175,100,0.75); display: inline-flex; align-items: center; flex-shrink: 0; margin-right: 4px; }

/* ── SKELETONS ── */
.sk-list { display: flex; flex-direction: column; gap: 10px; padding-top: 12px; margin-bottom: 32px; }
.sk-item { height: 60px; background: rgba(210,175,100,0.02); border: 1px solid rgba(210,175,100,0.08); animation: pulse 1.5s ease infinite; }
@keyframes pulse { 0%, 100% { opacity: 0.4; } 50% { opacity: 0.7; } }

/* ── LOGOUT ── */
.logout-wrap { padding: 32px 0 8px; }
.logout-btn { width: 100%; height: 48px; background: transparent; border: 1px solid rgba(180,70,70,0.35); font-family: 'Montserrat', sans-serif; font-size: 10px; font-weight: 600; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(200,90,90,0.75); cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 10px; transition: background 0.2s, border-color 0.2s; }
.logout-btn svg { width: 15px; height: 15px; }
.logout-btn:hover { background: rgba(200,60,60,0.06); border-color: rgba(200,80,80,0.6); }

/* ── DANGER ZONE ── */
.danger-zone { margin-top: 40px; padding-top: 28px; border-top: 1px solid rgba(200,80,80,0.15); }
.section-eyebrow.danger { color: rgba(200,90,90,0.55); }
.danger-text { font-size: 12px; font-weight: 300; line-height: 1.7; color: rgba(237,232,223,0.45); margin: 0 0 18px; }
.delete-account-btn { width: 100%; height: 48px; background: rgba(180,60,60,0.06); border: 1px solid rgba(180,70,70,0.4); font-family: 'Montserrat', sans-serif; font-size: 10px; font-weight: 600; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(210,110,100,0.9); cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 10px; transition: background 0.2s, border-color 0.2s; }
.delete-account-btn svg { width: 15px; height: 15px; }
.delete-account-btn:hover { background: rgba(200,60,60,0.12); border-color: rgba(200,90,90,0.7); }

/* ── DELETE CONFIRMATION MODAL ── */
.modal-overlay { position: fixed; inset: 0; z-index: 100; background: rgba(0,0,0,0.7); display: flex; align-items: center; justify-content: center; padding: 20px; }
.modal-panel { width: 100%; max-width: 420px; background: #0a0a0b; border: 1px solid rgba(200,80,80,0.3); padding: 28px 26px; }
.modal-title { font-family: 'Cormorant', serif; font-size: 22px; font-weight: 400; color: #edeae2; margin: 0 0 14px; }
.modal-text { font-size: 13px; font-weight: 300; line-height: 1.7; color: rgba(237,232,223,0.55); margin: 0 0 12px; }
.modal-confirm-label { margin-bottom: 10px; color: rgba(237,232,223,0.7); }
.modal-confirm-label strong { color: rgba(210,110,100,0.9); font-weight: 600; letter-spacing: 1px; }
.modal-input { width: 100%; height: 44px; background: rgba(255,255,255,0.03); border: 1px solid rgba(200,80,80,0.3); color: #edeae2; font-family: 'Montserrat', sans-serif; font-size: 14px; padding: 0 14px; outline: none; box-sizing: border-box; margin-bottom: 20px; }
.modal-input:focus { border-color: rgba(200,90,90,0.6); }
.modal-actions { display: flex; gap: 10px; }
.modal-cancel, .modal-confirm { flex: 1; height: 44px; font-family: 'Montserrat', sans-serif; font-size: 10px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; cursor: pointer; transition: background 0.2s, border-color 0.2s, opacity 0.2s; }
.modal-cancel { background: transparent; border: 1px solid rgba(237,232,223,0.2); color: rgba(237,232,223,0.6); }
.modal-cancel:hover { border-color: rgba(237,232,223,0.4); }
.modal-confirm { background: rgba(180,60,60,0.15); border: 1px solid rgba(200,80,80,0.5); color: rgba(230,150,140,0.95); }
.modal-confirm:hover:not(:disabled) { background: rgba(200,60,60,0.25); border-color: rgba(210,90,90,0.7); }
.modal-confirm:disabled { opacity: 0.35; cursor: not-allowed; }

/* ── BOTTOM NAV ── */
.bottom-nav { position: fixed; bottom: 0; left: 0; right: 0; z-index: 20; background: #070708; border-top: 1px solid rgba(210,175,100,0.1); display: flex; align-items: center; justify-content: space-around; padding: 14px 0 18px; }
.bottom-nav-glow { position: absolute; top: -1px; left: 10%; right: 10%; height: 1px; background: linear-gradient(90deg, transparent, rgba(210,175,100,0.2), transparent); pointer-events: none; }
.nav-item { display: flex; flex-direction: column; align-items: center; gap: 5px; cursor: pointer; padding: 4px 20px; color: rgba(237,232,223,0.2); transition: color 0.2s; background: none; border: none; font-family: 'Montserrat', sans-serif; min-width: 60px; }
.nav-item.active { color: rgba(210,175,100,0.8); }
.nav-item:hover:not(.active) { color: rgba(237,232,223,0.45); }
.nav-item svg { width: 18px; height: 18px; }
.nav-label { font-size: 9px; font-weight: 500; letter-spacing: 1.5px; text-transform: uppercase; }
.nav-dot { width: 3px; height: 3px; border-radius: 50%; background: rgba(210,175,100,0.7); margin-top: 1px; }

/* ── TOAST ── */
.toast { position: fixed; bottom: 100px; left: 50%; transform: translateX(-50%); background: #0f0e0c; border: 1px solid rgba(210,175,100,0.3); padding: 12px 22px; font-size: 11px; font-weight: 500; letter-spacing: 0.5px; color: #edeae2; z-index: 200; }
.toast.error { border-color: rgba(200,80,80,0.4); color: rgba(230,150,150,0.9); }
.toast.success { border-color: rgba(210,175,100,0.5); color: rgba(210,175,100,0.95); }

@media (max-width: 600px) { .page { padding: 32px 16px 120px; } .frame, .frame-inner { inset: 12px; } .section-title { font-size: 28px; } .profile-card { flex-direction: column; align-items: center; text-align: center; } .profile-meta-row { justify-content: center; } .quick-actions { justify-content: center; } }
@media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; } }
</style>